import { zodResolver } from "@hookform/resolvers/zod";
import { CalendarIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import BreadCrumb from "../../breadcrumb";
import Header from "../../DashboardNavigation/header";
import Sidebar from "../../DashboardNavigation/sidebar";
import { Button } from "../../ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../ui/form";
import { Heading } from "../../ui/heading";
import { Input } from "../../ui/input";
import { Separator } from "../../ui/separator";
import {
  PressFormValues,
  pressSchema,
  UpdatedPressSchema,
  updatedPressSchema,
} from "./press-zod.schema";

import {
  getPressById,
  handleCreatePress,
  handleUpdatePresse,
} from "../../../API/Presse/presseService";
import QuillEditor from "../../../lib/quill/quill";
import { cn } from "../../../lib/utils";
import PdfFileUpload from "../../FileUpload/PdfFileUpload";
import { Calendar } from "../../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";

export const PressForm: React.ComponentType<any> = () => {
  const uploadUrl = process.env.REACT_APP_FILE_UPLOAD;

  const router = useNavigate();
  const { id } = useParams();
  const initialData = id ? parseInt(id) : undefined;
  const [loading, setLoading] = useState(false);
  const title = initialData
    ? "Modifier la presse"
    : "Ajouter un document dans la presse";
  const description = initialData ? "Modifier la presse" : "";
  const action = initialData ? "Mise à jour" : "Ajouter";

  const form = useForm<PressFormValues | UpdatedPressSchema>({
    resolver: zodResolver(initialData ? updatedPressSchema : pressSchema),
  });

  const onSubmit = async () => {
    setLoading(true);
    const formData = form.getValues();
    try {
      if (initialData) {
        await handleUpdatePresse(initialData, formData, router);
      } else {
        await handleCreatePress(formData, router);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const [data, setData] = useState<any>();
  useEffect(() => {
    const fetchData = async () => {
      if (initialData) {
        const pressData = await getPressById(initialData);

        setData(pressData);
        form.reset({
          title: pressData.title,
          description: pressData.description,
          category: pressData.category,
          date: pressData.date ? new Date(pressData.date) : undefined,
          file: pressData.file
            ? {
                fileName: pressData.file,
                name: pressData.file,
                fileSize: 0,
                size: 0,
                key: pressData.file,
              }
            : undefined,
        });
      }
    };

    if (initialData) {
      fetchData();
    }
  }, [initialData]);

  const breadcrumbItems = [
    { title: "Presse", link: "/admin-dashboard/presse" },
    {
      title: initialData ? "Mise à jour" : "Ajouter",
      link: "/admin-dashboard/presse/ajouter",
    },
  ];

  const categories = [
    { id: 1, designation: "Communiquées" },
    { id: 2, designation: "Retombées" },
  ];

  return (
    <>
      <Header />
      <div className="flex h-screen ">
        <Sidebar />
        <div className="w-full p-10">
          <BreadCrumb items={breadcrumbItems} />
          <div className="flex items-center justify-between">
            <Heading title={title} description={description} />
          </div>
          <Separator />
          <Form {...form}>
            <form
              className="w-full space-y-8 overflow-y-auto"
              onSubmit={form.handleSubmit(onSubmit)}
              encType="multipart/form-data"
            >
              <div className="flex justify-between">
                <div className="w-1/2">
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Titre </FormLabel>
                        <FormControl>
                          <Input
                            disabled={loading}
                            placeholder={"Titre..."}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="w-1/4">
                  <FormField
                    control={form.control}
                    name="category"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Catégorie</FormLabel>
                        <Select
                          disabled={loading}
                          onValueChange={field.onChange}
                          value={field.value}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue
                                defaultValue={field.value}
                                placeholder="Sélectionnez une catégorie"
                              />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {categories?.map((cat) => (
                              <SelectItem key={cat.id} value={cat.designation}>
                                {cat.designation}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div>
                  <FormField
                    control={form.control}
                    name="date"
                    render={({ field }) => (
                      <FormItem className="flex flex-col mt-3">
                        <FormLabel>date de publication</FormLabel>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant={"outline"}
                              className={cn(
                                "w-[280px] justify-start text-left font-normal",
                                !field.value && "text-muted-foreground"
                              )}
                            >
                              <CalendarIcon className="w-4 h-4 mr-2" />
                              {field.value ? (
                                field.value.toLocaleDateString("fr-FR")
                              ) : (
                                <span>Date début</span>
                              )}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <Calendar
                              mode="single"
                              selected={field.value}
                              onSelect={(date) => {
                                field.onChange(date);
                              }}
                            />
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="w-full ">
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <QuillEditor
                          value={field.value || ""}
                          onChange={(value: string) => {
                            field.onChange(value);
                            form.trigger("description");
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {data?.file && (
                  <div className="mt-4 mb-4">
                    <a
                      href={`${uploadUrl}/presse/${data.file}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500 underline"
                    >
                      Consulter l'ancien PDF
                    </a>
                  </div>
                )}

                <FormField
                  control={form.control}
                  name="file"
                  render={() => (
                    <FormItem>
                      <FormControl>
                        <PdfFileUpload
                          onChange={(files) => {
                            const file = files[0];
                            if (file) {
                              const mappedFile = {
                                key: file.id,
                                fileName: file.fileName,
                                name: file.fileName,
                                fileSize: parseInt(file.fileSize),
                                size: parseInt(file.fileSize),
                                file: file.file,
                              };
                              form.setValue("file", mappedFile);
                            }
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  disabled={loading}
                  isLoading={loading}
                  className="ml-auto "
                  type="submit"
                >
                  {action}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </>
  );
};
